import * as math from 'mathjs'
import { numberToWords, type NumberToWordsOptions } from '@/lib/tools/numberToWords'
import { textCaseConverter, type TextCaseConverterOptions } from '@/lib/tools/textCaseConverter'
import { bmiCalculator, type BMICalculatorOptions } from '@/lib/tools/bmiCalculator'
import { lengthConverter, areaConverter } from '@/lib/tools/unitConverter'
import { volumeConverter, massConverter } from '@/lib/tools/volumeMassConverter'
import { speedConverter, timeUnitConverter } from '@/lib/tools/speedTimeConverter'
import { speedDistanceTime } from '@/lib/tools/speedDistanceTime'
import {
    hmsAddSubtract,
    timeArithmetic,
    hoursMinutesAddSubtract,
    decimalToTime,
    timeToDecimal,
    to24HourTime,
    to12HourTime,
    hoursBetweenClockTimes,
} from '@/lib/tools/timeDurationCalc'
import {
    dateAddSubtract,
    dateDifference,
    daysAndBusinessDays,
    datetimeDifference,
    ageCalculator,
    ageChecker,
    romanNumeralDate,
} from '@/lib/tools/dateCalc'
import { workHours, clockInOutTotal, shiftCoverage } from '@/lib/tools/workShiftCalc'
import { focusSessionPlanner, contextSwitchingCost, meetingTimezoneOverlap } from '@/lib/tools/productivityCalc'
import { sunriseSunset } from '@/lib/tools/sunriseSunset'
import {
    temperatureConverter,
    celsiusToFahrenheit,
    fahrenheitToCelsius,
    celsiusToKelvin,
    fahrenheitToKelvin,
    temperatureDifference,
} from '@/lib/tools/temperatureConverter'
import {
    angleConverter,
    degreesToRadians,
    radiansToDegrees,
    dmsToDecimalDegrees,
    angleAddSubtract,
    referenceCoterminalAngle,
} from '@/lib/tools/angleConverter'
import { dataStorageConverter, downloadTimeCalculator } from '@/lib/tools/dataStorageConverter'
import {
    numberBaseConverter,
    binaryToDecimal,
    decimalToBinary,
    decimalToHexadecimal,
    hexadecimalToDecimal,
    textToBinary,
    binaryToText,
} from '@/lib/tools/numberBaseConverter'
import { numberToRoman, romanToNumber } from '@/lib/tools/romanNumeralConverter'
import {
    roasCalculator,
    cpaCalculator,
    cpmCalculator,
    ctrCalculator,
    conversionRateCalculator,
    funnelConversionCalculator,
    multiTouchAttributionCalculator,
    ltvCacRatioCalculator,
    churnRateCalculator,
    loyaltyProgramRoiCalculator,
    organicTrafficValueCalculator,
    emailMarketingRoiCalculator,
} from '@/lib/tools/marketingCalculators'
import {
    concreteCalculator,
    concreteBagsCalculator,
    cubicYardsCalculator,
    squareFootageCalculator,
    gravelCalculator,
    mulchCalculator,
    tankCapacityCalculator,
    roadwayFillCalculator,
    sandCalculator,
    topsoilCalculator,
    paverCalculator,
    retainingWallBlockCalculator,
} from '@/lib/tools/constructionCalculators'
import {
    serpSnippetChecker,
    removeTrackingParams,
    utmUrlBuilder,
    readabilityScoreCalculator,
    tournamentBracketGenerator,
    platformLimitValidator,
    slugGenerator,
    duplicateLineRemover,
    textDiffChecker,
    metaTagGenerator,
    robotsTxtGenerator,
    loremIpsumGenerator,
} from '@/lib/tools/utilitiesCalculators'
import {
    wordCharacterCounter,
    readingTimeCalculator,
    randomNamePicker,
    keywordDensityAnalyzer,
    commonMisspellingsChecker,
    anagramChecker,
    palindromeChecker,
    pigLatinTranslator,
    acronymGenerator,
    randomWordGenerator,
    textReverser,
} from '@/lib/tools/wordTextTools'
import {
    percentageCalculator,
    percentChangeCalculator,
    percentIncreaseCalculator,
    percentDecreaseCalculator,
    reversePercentageCalculator,
    tipCalculator,
    salesTaxCalculator,
    discountCalculator,
    ratioSimplifier,
    proportionCalculator,
    aspectRatioCalculator,
    ratioToPercentageConverter,
} from '@/lib/tools/percentRatioCalculators'
import {
    dpiCalculator,
    posterSizeFinder,
    canvasPanelSplitter,
    matCalculator,
    bleedCalculator,
    safeMarginCalculator,
    photoPrintSizeFinder,
    socialCropCalculator,
    paperSizeConverter,
    scaleFactorCalculator,
    responsiveImageSizeCalculator,
    gridColumnWidthCalculator,
} from '@/lib/tools/printingDesignCalculators'
import {
    ppiCalculator,
    jwtExpirationCalculator,
    apiRateLimitCalculator,
    cronNextRunCalculator,
    passwordEntropyCalculator,
    pageLoadTimeCalculator,
    slaDowntimeCalculator,
    unixTimestampConverter,
    subnetCalculator,
    myIpRangeCalculator,
    aiTokenCostCalculator,
    deploymentRiskCalculator,
    colorContrastCalculator,
} from '@/lib/tools/developerToolsCalculators'
import {
    bingoNumberGenerator,
    lotteryNumberPicker,
    diceRoller,
    randomPasswordGenerator,
    sudokuPuzzleGenerator,
    yugiohLifePointCalculator,
    fuelEconomyCalculator,
    ipAddressDetector,
    coinFlipGenerator,
    magic8BallDecisionMaker,
    randomCardGenerator,
    randomTeamGenerator,
} from '@/lib/tools/gamesRandomizersCalculators'
import {
    meanMedianModeRangeCalculator,
    standardDeviationCalculator,
    varianceCalculator,
    zScoreCalculator,
    percentileCalculator,
    quartileIqrCalculator,
    fiveNumberSummaryCalculator,
    outlierCalculator,
    coefficientOfVariationCalculator,
    weightedAverageCalculator,
    geometricMeanCalculator,
    frequencyDistributionCalculator,
} from '@/lib/tools/descriptiveStatisticsCalculators'
import {
    basicProbabilityCalculator,
    combinationCalculator,
    permutationCalculator,
    factorialCalculator,
    binomialProbabilityCalculator,
    normalDistributionCalculator,
    poissonDistributionCalculator,
    expectedValueCalculator,
    randomNumberGeneratorTool,
    oddsConverter,
    conditionalProbabilityCalculator,
    diceRollProbabilityCalculator,
} from '@/lib/tools/probabilityRandomCalculators'
import {
    confidenceIntervalCalculator,
    sampleSizeCalculator,
    marginOfErrorCalculator,
    oneSampleTTestCalculator,
    twoSampleTTestCalculator,
    chiSquareGoodnessOfFitCalculator,
    pValueFromZCalculator,
    correlationCoefficientCalculator,
    linearRegressionCalculator,
    standardErrorCalculator,
    effectSizeCohensDCalculator,
    relativeRiskOddsRatioCalculator,
} from '@/lib/tools/statisticalAnalysisCalculators'
import {
    squareRootCalculator,
    cubeRootCalculator,
    nthRootCalculator,
    exponentCalculator,
    radicalSimplifier,
    scientificNotationCalculator,
    absoluteValueCalculator,
    logarithmCalculator,
    exponentialGrowthDecayCalculator,
    perfectSquareCubeChecker,
    rationalExponentCalculator,
    rootMeanSquareCalculator,
} from '@/lib/tools/powersRootsCalculators'
import {
    quadraticEquationSolver,
    linearEquationSolver,
    systemOfLinearEquationsSolver,
    slopeCalculator,
    distanceBetweenPointsCalculator,
    midpointCalculator,
    pointSlopeFormCalculator,
    parabolaVertexCalculator,
    discriminantCalculator,
    completingTheSquareCalculator,
    quadraticFactoringCalculator,
    binomialMultiplicationCalculator,
} from '@/lib/tools/equationsExpressionsCalculators'
import {
    longDivisionCalculator,
    longMultiplicationCalculator,
    longAdditionCalculator,
    longSubtractionCalculator,
    gcdCalculator,
    lcmCalculator,
    primeFactorizationCalculator,
    primeNumberChecker,
    moduloCalculator,
    divisibilityChecker,
    digitSumCalculator,
    roundingCalculator,
} from '@/lib/tools/arithmeticCalculators'
import {
    fractionSimplifier,
    fractionToDecimalCalculator,
    decimalToFractionCalculator,
    fractionToPercentCalculator,
    percentToFractionCalculator,
    addingFractionsCalculator,
    subtractingFractionsCalculator,
    multiplyingFractionsCalculator,
    dividingFractionsCalculator,
    mixedImproperConverter,
    comparingFractionsCalculator,
    fractionOfNumberCalculator,
} from '@/lib/tools/fractionsCalculators'
import {
    factorsCalculator,
    primeNumbersGenerator,
    oddEvenChecker,
    perfectNumberChecker,
    fibonacciSequenceCalculator,
    coprimeChecker,
    sumOfDivisorsCalculator,
    numberOfDivisorsCalculator,
    armstrongNumberChecker,
    triangularNumberCalculator,
    multiplesCalculator,
    palindromicNumberChecker,
} from '@/lib/tools/factorsNumberTheoryCalculators'
import {
    significantFiguresCalculator,
    roundToNearestMultipleCalculator,
    ceilingFloorCalculator,
    truncateNumberCalculator,
    engineeringNotationCalculator,
    orderOfMagnitudeCalculator,
    numberFormatterCalculator,
    ordinalNumberConverter,
    bankersRoundingCalculator,
    decimalPlacesCounter,
    roundToNearestCentCalculator,
    repeatingDecimalCalculator,
} from '@/lib/tools/roundingNotationCalculators'
import {
    bettingOddsConverter,
    parlayOddsCalculator,
    functionEvaluator,
    functionCompositionCalculator,
    inverseFunctionFinder,
    arithmeticSequenceCalculator,
    geometricSequenceCalculator,
    polynomialLongDivisionCalculator,
    setOperationsCalculator,
    truthTableGenerator,
    matrixCalculator2x2,
    absoluteValueEquationSolver,
} from '@/lib/tools/functionsToolsCalculators'
import {
    multifactorialCalculator,
    permutationParityChecker,
    circularPermutationsCalculator,
    pascalTriangleRowGenerator,
    combinationsWithRepetitionCalculator,
    permutationsWithRepetitionCalculator,
    derangementCalculator,
    catalanNumberCalculator,
    stirlingSecondKindCalculator,
    bellNumberCalculator,
    lucasNumbersCalculator,
    integerPartitionCalculator,
} from '@/lib/tools/discreteMathCalculators'
import {
    sineCalculator,
    cosineCalculator,
    tangentCalculator,
    cotSecCscCalculator,
    arcsinCalculator,
    arccosCalculator,
    arctanCalculator,
    rightTriangleSolver,
    lawOfSinesCalculator,
    lawOfCosinesCalculator,
    unitCircleCalculator,
    trigTableGenerator,
} from '@/lib/tools/trigonometryCalculators'
import {
    molecularWeightCalculator,
    percentCompositionCalculator,
    empiricalFormulaCalculator,
    molecularFormulaCalculator,
    molesGramsCalculator,
    molarityCalculator,
    dilutionCalculator,
    idealGasLawCalculator,
    phCalculator,
    elementLookupTool,
    densityCalculatorChem,
    limitingReactantCalculator,
} from '@/lib/tools/chemistryCalculators'
import {
    velocityCalculator,
    accelerationCalculator,
    displacementCalculator,
    finalVelocityCalculator,
    suvatThirdEquationCalculator,
    freeFallCalculator,
    projectileMotionCalculator,
    centripetalAccelerationCalculator,
    relativeVelocityCalculator,
    stoppingDistanceCalculator,
    terminalVelocityCalculator,
    generalKinematicsSolver,
} from '@/lib/tools/kinematicsCalculators'
import {
    forceCalculator,
    momentumCalculator,
    impulseCalculator,
    weightCalculator,
    frictionForceCalculator,
    inclineForceCalculator,
    workCalculator,
    kineticEnergyCalculator,
    potentialEnergyCalculator,
    powerCalculator,
    springForceCalculator,
    torqueCalculator,
} from '@/lib/tools/dynamicsCalculators'
import {
    mechanicalEnergyCalculator,
    workEnergyTheoremCalculator,
    heatEnergyCalculator,
    latentHeatCalculator,
    electricalEnergyCalculator,
    massEnergyEquivalenceCalculator,
    efficiencyCalculator,
    escapeVelocityCalculator,
    energyUnitConverter,
    rotationalKineticEnergyCalculator,
    windPowerCalculator,
    photonEnergyCalculator,
} from '@/lib/tools/energyCalculators'
import {
    waveSpeedCalculator,
    wavePeriodCalculator,
    dopplerEffectCalculator,
    speedOfSoundCalculator,
    decibelCalculator,
    snellsLawCalculator,
    standingWaveCalculator,
    buoyancyCalculator,
    hydrostaticPressureCalculator,
    boylesLawCalculator,
    specificGravityCalculator,
    youngsModulusCalculator,
} from '@/lib/tools/wavesMatterCalculators'
import {
    cubeCalculator,
    rectangularPrismCalculator,
    cylinderCalculator,
    coneCalculator,
    sphereCalculator,
    squarePyramidCalculator,
    triangularPrismCalculator,
    hemisphereCalculator,
    coneFrustumCalculator,
    ellipsoidCalculator,
    capsuleCalculator,
    torusCalculator,
} from '@/lib/tools/solidGeometryCalculators'
import {
    triangleAreaCalculator,
    pythagoreanTheoremCalculator,
    circleCalculator,
    circleSectorCalculator,
    rectangleCalculator,
    squareCalculator,
    parallelogramCalculator,
    trapezoidCalculator,
    rhombusCalculator,
    regularPolygonCalculator,
    ellipseCalculator,
    kiteCalculator,
} from '@/lib/tools/planeGeometryCalculators'

export interface JsonEngineInput {
    [key: string]: number | string
}

export interface JsonEngineOutput {
    [key: string]: number | string
}

export interface JsonEngineConfig {
    inputs: Array<{
        key: string
        default?: number | string
    }>
    formulas?: Record<string, string>
    functions?: Record<string, {
        type: 'function'
        functionName: string
        params: Record<string, string>  // Маппинг: имя параметра функции -> ключ input
    }>
    outputs: Array<{
        key: string
        precision?: number
    }>
    language?: string  // Язык для локализации (en, ru, de, es, fr, it, pl, lv)
}

/**
 * Реестр кастомных функций
 */
const FUNCTION_REGISTRY: Record<string, (params: Record<string, any>) => any> = {
    numberToWords: (params: Record<string, any>) => {
        const { value, mode, currency, vatRate, textCase, language } = params
        const options: NumberToWordsOptions = {
            mode: mode || 'words',
            currency: currency || 'USD',
            vatRate: vatRate ? parseFloat(String(vatRate)) : undefined,
            textCase: textCase || 'Sentence case',
            language: language || 'en'
        }
        return numberToWords(value, options)
    },
    textCaseConverter: (params: Record<string, any>) => {
        const { text, mode } = params
        const options: TextCaseConverterOptions = {
            mode: mode || 'lower'
        }
        return textCaseConverter(text, options)
    },
    bmiCalculator: (params: Record<string, any>) => {
        const {
            age,
            gender,
            height_unit,
            height_value,
            height_ft,
            height_in,
            height_m,
            height_cm,
            weight_unit,
            weight_value
        } = params

        const options: BMICalculatorOptions = {
            age: age ? Number(age) : 25,
            gender: gender || 'male',
            height_unit: height_unit || 'ft_in',
            height_value: height_value !== undefined ? Number(height_value) : undefined,
            height_ft: height_ft !== undefined ? Number(height_ft) : undefined,
            height_in: height_in !== undefined ? Number(height_in) : undefined,
            height_m: height_m !== undefined ? Number(height_m) : undefined,
            height_cm: height_cm !== undefined ? Number(height_cm) : undefined,
            weight_unit: weight_unit || 'lb',
            weight_value: weight_value ? Number(weight_value) : 160
        }

        return bmiCalculator(options)
    },
    lengthConverter: (params: Record<string, any>) => {
        return lengthConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit })
    },
    areaConverter: (params: Record<string, any>) => {
        return areaConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit })
    },
    volumeConverter: (params: Record<string, any>) => {
        return volumeConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit })
    },
    massConverter: (params: Record<string, any>) => {
        return massConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit })
    },
    speedConverter: (params: Record<string, any>) => {
        return speedConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit })
    },
    timeUnitConverter: (params: Record<string, any>) => {
        return timeUnitConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit })
    },
    speedDistanceTime: (params: Record<string, any>) => {
        return speedDistanceTime({ solve_for: params.solve_for, distance: params.distance, speed: params.speed, time: params.time })
    },
    hmsAddSubtract: (params: Record<string, any>) => {
        return hmsAddSubtract(params as any)
    },
    timeArithmetic: (params: Record<string, any>) => {
        return timeArithmetic(params as any)
    },
    hoursMinutesAddSubtract: (params: Record<string, any>) => {
        return hoursMinutesAddSubtract(params as any)
    },
    decimalToTime: (params: Record<string, any>) => {
        return decimalToTime({ decimal_hours: params.decimal_hours })
    },
    timeToDecimal: (params: Record<string, any>) => {
        return timeToDecimal({ hours: params.hours, minutes: params.minutes, seconds: params.seconds })
    },
    to24HourTime: (params: Record<string, any>) => {
        return to24HourTime({ hour: params.hour, minute: params.minute, ampm: params.ampm })
    },
    to12HourTime: (params: Record<string, any>) => {
        return to12HourTime({ hour_24: params.hour_24, minute: params.minute })
    },
    hoursBetweenClockTimes: (params: Record<string, any>) => {
        return hoursBetweenClockTimes(params as any)
    },
    dateAddSubtract: (params: Record<string, any>) => {
        return dateAddSubtract({ base_date: params.base_date, amount: params.amount, unit: params.unit, operation: params.operation })
    },
    dateDifference: (params: Record<string, any>) => {
        return dateDifference({ date1: params.date1, date2: params.date2 })
    },
    daysAndBusinessDays: (params: Record<string, any>) => {
        return daysAndBusinessDays({ date1: params.date1, date2: params.date2 })
    },
    datetimeDifference: (params: Record<string, any>) => {
        return datetimeDifference(params as any)
    },
    ageCalculator: (params: Record<string, any>) => {
        return ageCalculator({ birth_date: params.birth_date, as_of_date: params.as_of_date })
    },
    ageChecker: (params: Record<string, any>) => {
        return ageChecker({ birth_date: params.birth_date, required_age: params.required_age, as_of_date: params.as_of_date })
    },
    romanNumeralDate: (params: Record<string, any>) => {
        return romanNumeralDate({ date: params.date })
    },
    workHours: (params: Record<string, any>) => {
        return workHours(params as any)
    },
    clockInOutTotal: (params: Record<string, any>) => {
        return clockInOutTotal(params as any)
    },
    shiftCoverage: (params: Record<string, any>) => {
        return shiftCoverage(params as any)
    },
    focusSessionPlanner: (params: Record<string, any>) => {
        return focusSessionPlanner({ total_minutes: params.total_minutes, focus_minutes: params.focus_minutes, break_minutes: params.break_minutes })
    },
    contextSwitchingCost: (params: Record<string, any>) => {
        return contextSwitchingCost({ switches_per_day: params.switches_per_day, cost_per_switch_minutes: params.cost_per_switch_minutes, work_days_per_week: params.work_days_per_week })
    },
    meetingTimezoneOverlap: (params: Record<string, any>) => {
        return meetingTimezoneOverlap(params as any)
    },
    sunriseSunset: (params: Record<string, any>) => {
        return sunriseSunset({ latitude: params.latitude, longitude: params.longitude, date: params.date, utc_offset: params.utc_offset })
    },
    temperatureConverter: (params: Record<string, any>) => {
        return temperatureConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit })
    },
    celsiusToFahrenheit: (params: Record<string, any>) => {
        return celsiusToFahrenheit({ value: params.value })
    },
    fahrenheitToCelsius: (params: Record<string, any>) => {
        return fahrenheitToCelsius({ value: params.value })
    },
    celsiusToKelvin: (params: Record<string, any>) => {
        return celsiusToKelvin({ value: params.value })
    },
    fahrenheitToKelvin: (params: Record<string, any>) => {
        return fahrenheitToKelvin({ value: params.value })
    },
    temperatureDifference: (params: Record<string, any>) => {
        return temperatureDifference({ temp1: params.temp1, temp2: params.temp2, unit: params.unit })
    },
    angleConverter: (params: Record<string, any>) => {
        return angleConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit })
    },
    degreesToRadians: (params: Record<string, any>) => {
        return degreesToRadians({ value: params.value })
    },
    radiansToDegrees: (params: Record<string, any>) => {
        return radiansToDegrees({ value: params.value })
    },
    dmsToDecimalDegrees: (params: Record<string, any>) => {
        return dmsToDecimalDegrees({ degrees: params.degrees, minutes: params.minutes, seconds: params.seconds })
    },
    angleAddSubtract: (params: Record<string, any>) => {
        return angleAddSubtract(params as any)
    },
    referenceCoterminalAngle: (params: Record<string, any>) => {
        return referenceCoterminalAngle({ angle: params.angle })
    },
    dataStorageConverter: (params: Record<string, any>) => {
        return dataStorageConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit })
    },
    downloadTimeCalculator: (params: Record<string, any>) => {
        return downloadTimeCalculator({ file_size: params.file_size, file_size_unit: params.file_size_unit, speed: params.speed, speed_unit: params.speed_unit })
    },
    numberBaseConverter: (params: Record<string, any>) => {
        return numberBaseConverter({ value: params.value, from_base: params.from_base, to_base: params.to_base })
    },
    binaryToDecimal: (params: Record<string, any>) => {
        return binaryToDecimal({ value: params.value })
    },
    decimalToBinary: (params: Record<string, any>) => {
        return decimalToBinary({ value: params.value })
    },
    decimalToHexadecimal: (params: Record<string, any>) => {
        return decimalToHexadecimal({ value: params.value })
    },
    hexadecimalToDecimal: (params: Record<string, any>) => {
        return hexadecimalToDecimal({ value: params.value })
    },
    textToBinary: (params: Record<string, any>) => {
        return textToBinary({ value: params.value })
    },
    binaryToText: (params: Record<string, any>) => {
        return binaryToText({ value: params.value })
    },
    numberToRoman: (params: Record<string, any>) => {
        return numberToRoman({ value: params.value })
    },
    romanToNumber: (params: Record<string, any>) => {
        return romanToNumber({ value: params.value })
    },
    roasCalculator: (params: Record<string, any>) => {
        return roasCalculator({ revenue: params.revenue, ad_spend: params.ad_spend })
    },
    cpaCalculator: (params: Record<string, any>) => {
        return cpaCalculator({ total_spend: params.total_spend, conversions: params.conversions })
    },
    cpmCalculator: (params: Record<string, any>) => {
        return cpmCalculator({ total_spend: params.total_spend, impressions: params.impressions })
    },
    ctrCalculator: (params: Record<string, any>) => {
        return ctrCalculator({ clicks: params.clicks, impressions: params.impressions })
    },
    conversionRateCalculator: (params: Record<string, any>) => {
        return conversionRateCalculator({ conversions: params.conversions, visitors: params.visitors })
    },
    funnelConversionCalculator: (params: Record<string, any>) => {
        return funnelConversionCalculator({ stage1: params.stage1, stage2: params.stage2, stage3: params.stage3, stage4: params.stage4 })
    },
    multiTouchAttributionCalculator: (params: Record<string, any>) => {
        return multiTouchAttributionCalculator({ conversion_value: params.conversion_value, model: params.model })
    },
    ltvCacRatioCalculator: (params: Record<string, any>) => {
        return ltvCacRatioCalculator({ ltv: params.ltv, cac: params.cac, language: params.language })
    },
    churnRateCalculator: (params: Record<string, any>) => {
        return churnRateCalculator({ customers_start: params.customers_start, customers_lost: params.customers_lost })
    },
    loyaltyProgramRoiCalculator: (params: Record<string, any>) => {
        return loyaltyProgramRoiCalculator({ program_cost: params.program_cost, incremental_revenue: params.incremental_revenue })
    },
    organicTrafficValueCalculator: (params: Record<string, any>) => {
        return organicTrafficValueCalculator({ organic_visitors: params.organic_visitors, equivalent_cpc: params.equivalent_cpc })
    },
    emailMarketingRoiCalculator: (params: Record<string, any>) => {
        return emailMarketingRoiCalculator({ campaign_revenue: params.campaign_revenue, campaign_cost: params.campaign_cost })
    },
    concreteCalculator: (params: Record<string, any>) => {
        return concreteCalculator({ length: params.length, width: params.width, thickness: params.thickness, unit_system: params.unit_system })
    },
    concreteBagsCalculator: (params: Record<string, any>) => {
        return concreteBagsCalculator({ volume: params.volume, unit_system: params.unit_system })
    },
    cubicYardsCalculator: (params: Record<string, any>) => {
        return cubicYardsCalculator({ length: params.length, width: params.width, depth: params.depth, unit_system: params.unit_system })
    },
    squareFootageCalculator: (params: Record<string, any>) => {
        return squareFootageCalculator({ length: params.length, width: params.width, unit_system: params.unit_system })
    },
    gravelCalculator: (params: Record<string, any>) => {
        return gravelCalculator({ length: params.length, width: params.width, depth: params.depth, unit_system: params.unit_system })
    },
    mulchCalculator: (params: Record<string, any>) => {
        return mulchCalculator({ length: params.length, width: params.width, depth: params.depth, unit_system: params.unit_system })
    },
    tankCapacityCalculator: (params: Record<string, any>) => {
        return tankCapacityCalculator({ shape: params.shape, diameter: params.diameter, length: params.length, width: params.width, height: params.height, unit_system: params.unit_system })
    },
    roadwayFillCalculator: (params: Record<string, any>) => {
        return roadwayFillCalculator({ length: params.length, width: params.width, depth: params.depth, unit_system: params.unit_system })
    },
    sandCalculator: (params: Record<string, any>) => {
        return sandCalculator({ length: params.length, width: params.width, depth: params.depth, unit_system: params.unit_system })
    },
    topsoilCalculator: (params: Record<string, any>) => {
        return topsoilCalculator({ length: params.length, width: params.width, depth: params.depth, unit_system: params.unit_system })
    },
    paverCalculator: (params: Record<string, any>) => {
        return paverCalculator({ area: params.area, paver_length: params.paver_length, paver_width: params.paver_width, unit_system: params.unit_system })
    },
    retainingWallBlockCalculator: (params: Record<string, any>) => {
        return retainingWallBlockCalculator({ wall_length: params.wall_length, wall_height: params.wall_height, block_length: params.block_length, block_height: params.block_height, unit_system: params.unit_system })
    },
    serpSnippetChecker: (params: Record<string, any>) => {
        return serpSnippetChecker({ title: params.title, meta_description: params.meta_description, language: params.language })
    },
    removeTrackingParams: (params: Record<string, any>) => {
        return removeTrackingParams({ url: params.url, language: params.language })
    },
    utmUrlBuilder: (params: Record<string, any>) => {
        return utmUrlBuilder({ base_url: params.base_url, source: params.source, medium: params.medium, campaign: params.campaign, term: params.term, content: params.content, language: params.language })
    },
    readabilityScoreCalculator: (params: Record<string, any>) => {
        return readabilityScoreCalculator({ text: params.text, language: params.language })
    },
    tournamentBracketGenerator: (params: Record<string, any>) => {
        return tournamentBracketGenerator({ team_names: params.team_names, language: params.language })
    },
    platformLimitValidator: (params: Record<string, any>) => {
        return platformLimitValidator({ text: params.text, platform: params.platform, language: params.language })
    },
    slugGenerator: (params: Record<string, any>) => {
        return slugGenerator({ text: params.text })
    },
    duplicateLineRemover: (params: Record<string, any>) => {
        return duplicateLineRemover({ text: params.text, language: params.language })
    },
    textDiffChecker: (params: Record<string, any>) => {
        return textDiffChecker({ text_a: params.text_a, text_b: params.text_b, language: params.language })
    },
    metaTagGenerator: (params: Record<string, any>) => {
        return metaTagGenerator({ title: params.title, description: params.description, url: params.url, image_url: params.image_url, site_name: params.site_name })
    },
    robotsTxtGenerator: (params: Record<string, any>) => {
        return robotsTxtGenerator({ allow_all: params.allow_all, disallow_paths: params.disallow_paths, sitemap_url: params.sitemap_url, user_agent: params.user_agent })
    },
    loremIpsumGenerator: (params: Record<string, any>) => {
        return loremIpsumGenerator({ paragraphs: params.paragraphs })
    },
    wordCharacterCounter: (params: Record<string, any>) => {
        return wordCharacterCounter({ text: params.text })
    },
    readingTimeCalculator: (params: Record<string, any>) => {
        return readingTimeCalculator({ text: params.text, wpm: params.wpm })
    },
    randomNamePicker: (params: Record<string, any>) => {
        return randomNamePicker({ names: params.names, count: params.count, language: params.language })
    },
    keywordDensityAnalyzer: (params: Record<string, any>) => {
        return keywordDensityAnalyzer({ text: params.text, language: params.language })
    },
    commonMisspellingsChecker: (params: Record<string, any>) => {
        return commonMisspellingsChecker({ text: params.text, language: params.language })
    },
    anagramChecker: (params: Record<string, any>) => {
        return anagramChecker({ text_a: params.text_a, text_b: params.text_b, language: params.language })
    },
    palindromeChecker: (params: Record<string, any>) => {
        return palindromeChecker({ text: params.text, language: params.language })
    },
    pigLatinTranslator: (params: Record<string, any>) => {
        return pigLatinTranslator({ text: params.text })
    },
    acronymGenerator: (params: Record<string, any>) => {
        return acronymGenerator({ text: params.text })
    },
    randomWordGenerator: (params: Record<string, any>) => {
        return randomWordGenerator({ count: params.count })
    },
    textReverser: (params: Record<string, any>) => {
        return textReverser({ text: params.text, mode: params.mode })
    },
    percentageCalculator: (params: Record<string, any>) => {
        return percentageCalculator({ percent: params.percent, base_number: params.base_number })
    },
    percentChangeCalculator: (params: Record<string, any>) => {
        return percentChangeCalculator({ old_value: params.old_value, new_value: params.new_value, language: params.language })
    },
    percentIncreaseCalculator: (params: Record<string, any>) => {
        return percentIncreaseCalculator({ original_value: params.original_value, percent: params.percent })
    },
    percentDecreaseCalculator: (params: Record<string, any>) => {
        return percentDecreaseCalculator({ original_value: params.original_value, percent: params.percent })
    },
    reversePercentageCalculator: (params: Record<string, any>) => {
        return reversePercentageCalculator({ part_value: params.part_value, percent: params.percent })
    },
    tipCalculator: (params: Record<string, any>) => {
        return tipCalculator({ bill_amount: params.bill_amount, tip_percent: params.tip_percent, num_people: params.num_people })
    },
    salesTaxCalculator: (params: Record<string, any>) => {
        return salesTaxCalculator({ price: params.price, tax_rate: params.tax_rate })
    },
    discountCalculator: (params: Record<string, any>) => {
        return discountCalculator({ original_price: params.original_price, discount_percent: params.discount_percent })
    },
    ratioSimplifier: (params: Record<string, any>) => {
        return ratioSimplifier({ ratio_a: params.ratio_a, ratio_b: params.ratio_b })
    },
    proportionCalculator: (params: Record<string, any>) => {
        return proportionCalculator({ a: params.a, b: params.b, c: params.c, d: params.d, solve_for: params.solve_for })
    },
    aspectRatioCalculator: (params: Record<string, any>) => {
        return aspectRatioCalculator({ width: params.width, height: params.height, target_value: params.target_value, solve_for: params.solve_for })
    },
    ratioToPercentageConverter: (params: Record<string, any>) => {
        return ratioToPercentageConverter({ ratio_a: params.ratio_a, ratio_b: params.ratio_b })
    },
    dpiCalculator: (params: Record<string, any>) => {
        return dpiCalculator({ pixel_width: params.pixel_width, pixel_height: params.pixel_height, print_width: params.print_width, print_height: params.print_height, unit: params.unit, language: params.language })
    },
    posterSizeFinder: (params: Record<string, any>) => {
        return posterSizeFinder({ pixel_width: params.pixel_width, pixel_height: params.pixel_height, target_dpi: params.target_dpi, unit: params.unit })
    },
    canvasPanelSplitter: (params: Record<string, any>) => {
        return canvasPanelSplitter({ total_width: params.total_width, total_height: params.total_height, num_panels: params.num_panels, gap: params.gap })
    },
    matCalculator: (params: Record<string, any>) => {
        return matCalculator({ frame_width: params.frame_width, frame_height: params.frame_height, image_width: params.image_width, image_height: params.image_height })
    },
    bleedCalculator: (params: Record<string, any>) => {
        return bleedCalculator({ trim_width: params.trim_width, trim_height: params.trim_height, bleed_amount: params.bleed_amount })
    },
    safeMarginCalculator: (params: Record<string, any>) => {
        return safeMarginCalculator({ trim_width: params.trim_width, trim_height: params.trim_height, safe_margin: params.safe_margin })
    },
    photoPrintSizeFinder: (params: Record<string, any>) => {
        return photoPrintSizeFinder({ pixel_width: params.pixel_width, pixel_height: params.pixel_height, language: params.language })
    },
    socialCropCalculator: (params: Record<string, any>) => {
        return socialCropCalculator({ pixel_width: params.pixel_width, pixel_height: params.pixel_height, platform: params.platform })
    },
    paperSizeConverter: (params: Record<string, any>) => {
        return paperSizeConverter({ paper_size: params.paper_size, output_unit: params.output_unit, dpi: params.dpi })
    },
    scaleFactorCalculator: (params: Record<string, any>) => {
        return scaleFactorCalculator({ original_width: params.original_width, original_height: params.original_height, mode: params.mode, target_value: params.target_value })
    },
    responsiveImageSizeCalculator: (params: Record<string, any>) => {
        return responsiveImageSizeCalculator({ max_display_width: params.max_display_width })
    },
    gridColumnWidthCalculator: (params: Record<string, any>) => {
        return gridColumnWidthCalculator({ total_width: params.total_width, num_columns: params.num_columns, gutter_width: params.gutter_width })
    },
    ppiCalculator: (params: Record<string, any>) => {
        return ppiCalculator({ screen_width_px: params.screen_width_px, screen_height_px: params.screen_height_px, diagonal_inches: params.diagonal_inches })
    },
    jwtExpirationCalculator: (params: Record<string, any>) => {
        return jwtExpirationCalculator({ jwt_token: params.jwt_token, language: params.language })
    },
    apiRateLimitCalculator: (params: Record<string, any>) => {
        return apiRateLimitCalculator({ limit: params.limit, window_minutes: params.window_minutes, current_usage: params.current_usage })
    },
    cronNextRunCalculator: (params: Record<string, any>) => {
        return cronNextRunCalculator({ cron_expression: params.cron_expression, language: params.language })
    },
    passwordEntropyCalculator: (params: Record<string, any>) => {
        return passwordEntropyCalculator({ password: params.password, guesses_per_second: params.guesses_per_second, language: params.language })
    },
    pageLoadTimeCalculator: (params: Record<string, any>) => {
        return pageLoadTimeCalculator({ page_size_mb: params.page_size_mb, connection_speed_mbps: params.connection_speed_mbps, language: params.language })
    },
    slaDowntimeCalculator: (params: Record<string, any>) => {
        return slaDowntimeCalculator({ uptime_percent: params.uptime_percent })
    },
    unixTimestampConverter: (params: Record<string, any>) => {
        return unixTimestampConverter({ mode: params.mode, unix_timestamp: params.unix_timestamp, date_string: params.date_string })
    },
    subnetCalculator: (params: Record<string, any>) => {
        return subnetCalculator({ ip_address: params.ip_address, cidr_prefix: params.cidr_prefix })
    },
    myIpRangeCalculator: (params: Record<string, any>) => {
        return myIpRangeCalculator({ client_ip: params.client_ip, cidr_prefix: params.cidr_prefix, language: params.language })
    },
    aiTokenCostCalculator: (params: Record<string, any>) => {
        return aiTokenCostCalculator({ input_text: params.input_text, price_per_million_tokens: params.price_per_million_tokens, num_requests: params.num_requests })
    },
    deploymentRiskCalculator: (params: Record<string, any>) => {
        return deploymentRiskCalculator({ deployments_per_month: params.deployments_per_month, failed_deployments: params.failed_deployments, language: params.language })
    },
    colorContrastCalculator: (params: Record<string, any>) => {
        return colorContrastCalculator({ foreground_hex: params.foreground_hex, background_hex: params.background_hex, language: params.language })
    },
    bingoNumberGenerator: (params: Record<string, any>) => {
        return bingoNumberGenerator({ num_balls: params.num_balls })
    },
    lotteryNumberPicker: (params: Record<string, any>) => {
        return lotteryNumberPicker({ main_count: params.main_count, main_max: params.main_max, bonus_max: params.bonus_max, language: params.language })
    },
    diceRoller: (params: Record<string, any>) => {
        return diceRoller({ num_dice: params.num_dice, sides: params.sides, modifier: params.modifier })
    },
    randomPasswordGenerator: (params: Record<string, any>) => {
        return randomPasswordGenerator({ length: params.length, include_uppercase: params.include_uppercase, include_numbers: params.include_numbers, include_symbols: params.include_symbols })
    },
    sudokuPuzzleGenerator: (params: Record<string, any>) => {
        return sudokuPuzzleGenerator({ difficulty: params.difficulty })
    },
    yugiohLifePointCalculator: (params: Record<string, any>) => {
        return yugiohLifePointCalculator({ p1_starting_lp: params.p1_starting_lp, p1_change: params.p1_change, p2_starting_lp: params.p2_starting_lp, p2_change: params.p2_change, language: params.language })
    },
    fuelEconomyCalculator: (params: Record<string, any>) => {
        return fuelEconomyCalculator({ distance: params.distance, fuel_used: params.fuel_used, mode: params.mode, language: params.language })
    },
    ipAddressDetector: (params: Record<string, any>) => {
        return ipAddressDetector({ client_ip: params.client_ip })
    },
    coinFlipGenerator: (params: Record<string, any>) => {
        return coinFlipGenerator({ num_flips: params.num_flips, language: params.language })
    },
    magic8BallDecisionMaker: (params: Record<string, any>) => {
        return magic8BallDecisionMaker({ question: params.question, language: params.language })
    },
    randomCardGenerator: (params: Record<string, any>) => {
        return randomCardGenerator({ num_cards: params.num_cards, allow_duplicates: params.allow_duplicates, language: params.language })
    },
    randomTeamGenerator: (params: Record<string, any>) => {
        return randomTeamGenerator({ names_list: params.names_list, num_teams: params.num_teams, language: params.language })
    },
    meanMedianModeRangeCalculator: (params: Record<string, any>) => {
        return meanMedianModeRangeCalculator({ data: params.data, language: params.language })
    },
    standardDeviationCalculator: (params: Record<string, any>) => {
        return standardDeviationCalculator({ data: params.data, type: params.type })
    },
    varianceCalculator: (params: Record<string, any>) => {
        return varianceCalculator({ data: params.data, type: params.type })
    },
    zScoreCalculator: (params: Record<string, any>) => {
        return zScoreCalculator({ value: params.value, mean: params.mean, standard_deviation: params.standard_deviation, language: params.language })
    },
    percentileCalculator: (params: Record<string, any>) => {
        return percentileCalculator({ data: params.data, percentile: params.percentile })
    },
    quartileIqrCalculator: (params: Record<string, any>) => {
        return quartileIqrCalculator({ data: params.data })
    },
    fiveNumberSummaryCalculator: (params: Record<string, any>) => {
        return fiveNumberSummaryCalculator({ data: params.data })
    },
    outlierCalculator: (params: Record<string, any>) => {
        return outlierCalculator({ data: params.data, language: params.language })
    },
    coefficientOfVariationCalculator: (params: Record<string, any>) => {
        return coefficientOfVariationCalculator({ data: params.data, language: params.language })
    },
    weightedAverageCalculator: (params: Record<string, any>) => {
        return weightedAverageCalculator({ values_list: params.values_list, weights_list: params.weights_list })
    },
    geometricMeanCalculator: (params: Record<string, any>) => {
        return geometricMeanCalculator({ data: params.data })
    },
    frequencyDistributionCalculator: (params: Record<string, any>) => {
        return frequencyDistributionCalculator({ data: params.data })
    },
    basicProbabilityCalculator: (params: Record<string, any>) => {
        return basicProbabilityCalculator({ favorable: params.favorable, total: params.total, language: params.language })
    },
    combinationCalculator: (params: Record<string, any>) => {
        return combinationCalculator({ n: params.n, r: params.r })
    },
    permutationCalculator: (params: Record<string, any>) => {
        return permutationCalculator({ n: params.n, r: params.r })
    },
    factorialCalculator: (params: Record<string, any>) => {
        return factorialCalculator({ n: params.n })
    },
    binomialProbabilityCalculator: (params: Record<string, any>) => {
        return binomialProbabilityCalculator({ trials: params.trials, probability: params.probability, successes: params.successes })
    },
    normalDistributionCalculator: (params: Record<string, any>) => {
        return normalDistributionCalculator({ value: params.value, mean: params.mean, standard_deviation: params.standard_deviation })
    },
    poissonDistributionCalculator: (params: Record<string, any>) => {
        return poissonDistributionCalculator({ lambda: params.lambda, occurrences: params.occurrences })
    },
    expectedValueCalculator: (params: Record<string, any>) => {
        return expectedValueCalculator({ values_list: params.values_list, probabilities_list: params.probabilities_list })
    },
    randomNumberGeneratorTool: (params: Record<string, any>) => {
        return randomNumberGeneratorTool({ min: params.min, max: params.max, count: params.count, allow_duplicates: params.allow_duplicates, number_type: params.number_type })
    },
    oddsConverter: (params: Record<string, any>) => {
        return oddsConverter({ mode: params.mode, probability_percent: params.probability_percent, odds_for: params.odds_for, odds_against: params.odds_against })
    },
    conditionalProbabilityCalculator: (params: Record<string, any>) => {
        return conditionalProbabilityCalculator({ p_a_and_b: params.p_a_and_b, p_b: params.p_b })
    },
    diceRollProbabilityCalculator: (params: Record<string, any>) => {
        return diceRollProbabilityCalculator({ num_dice: params.num_dice, sides: params.sides, target_sum: params.target_sum })
    },
    confidenceIntervalCalculator: (params: Record<string, any>) => {
        return confidenceIntervalCalculator({ sample_mean: params.sample_mean, sample_std_dev: params.sample_std_dev, sample_size: params.sample_size, confidence_level: params.confidence_level })
    },
    sampleSizeCalculator: (params: Record<string, any>) => {
        return sampleSizeCalculator({ confidence_level: params.confidence_level, margin_of_error: params.margin_of_error, population_proportion: params.population_proportion, population_size: params.population_size })
    },
    marginOfErrorCalculator: (params: Record<string, any>) => {
        return marginOfErrorCalculator({ sample_size: params.sample_size, sample_proportion: params.sample_proportion, confidence_level: params.confidence_level })
    },
    oneSampleTTestCalculator: (params: Record<string, any>) => {
        return oneSampleTTestCalculator({ sample_mean: params.sample_mean, hypothesized_mean: params.hypothesized_mean, sample_std_dev: params.sample_std_dev, sample_size: params.sample_size, tail: params.tail })
    },
    twoSampleTTestCalculator: (params: Record<string, any>) => {
        return twoSampleTTestCalculator({ mean1: params.mean1, std_dev1: params.std_dev1, sample_size1: params.sample_size1, mean2: params.mean2, std_dev2: params.std_dev2, sample_size2: params.sample_size2, tail: params.tail })
    },
    chiSquareGoodnessOfFitCalculator: (params: Record<string, any>) => {
        return chiSquareGoodnessOfFitCalculator({ observed_values: params.observed_values, expected_values: params.expected_values })
    },
    pValueFromZCalculator: (params: Record<string, any>) => {
        return pValueFromZCalculator({ z_score: params.z_score, tail: params.tail })
    },
    correlationCoefficientCalculator: (params: Record<string, any>) => {
        return correlationCoefficientCalculator({ x_values: params.x_values, y_values: params.y_values, language: params.language })
    },
    linearRegressionCalculator: (params: Record<string, any>) => {
        return linearRegressionCalculator({ x_values: params.x_values, y_values: params.y_values })
    },
    standardErrorCalculator: (params: Record<string, any>) => {
        return standardErrorCalculator({ sample_std_dev: params.sample_std_dev, sample_size: params.sample_size })
    },
    effectSizeCohensDCalculator: (params: Record<string, any>) => {
        return effectSizeCohensDCalculator({ mean1: params.mean1, std_dev1: params.std_dev1, sample_size1: params.sample_size1, mean2: params.mean2, std_dev2: params.std_dev2, sample_size2: params.sample_size2, language: params.language })
    },
    relativeRiskOddsRatioCalculator: (params: Record<string, any>) => {
        return relativeRiskOddsRatioCalculator({ exposed_with_event: params.exposed_with_event, exposed_without_event: params.exposed_without_event, unexposed_with_event: params.unexposed_with_event, unexposed_without_event: params.unexposed_without_event })
    },
    squareRootCalculator: (params: Record<string, any>) => {
        return squareRootCalculator({ number: params.number })
    },
    cubeRootCalculator: (params: Record<string, any>) => {
        return cubeRootCalculator({ number: params.number })
    },
    nthRootCalculator: (params: Record<string, any>) => {
        return nthRootCalculator({ number: params.number, degree: params.degree })
    },
    exponentCalculator: (params: Record<string, any>) => {
        return exponentCalculator({ base: params.base, exponent: params.exponent })
    },
    radicalSimplifier: (params: Record<string, any>) => {
        return radicalSimplifier({ radicand: params.radicand, degree: params.degree })
    },
    scientificNotationCalculator: (params: Record<string, any>) => {
        return scientificNotationCalculator({ mode: params.mode, number: params.number, coefficient: params.coefficient, notation_exponent: params.notation_exponent })
    },
    absoluteValueCalculator: (params: Record<string, any>) => {
        return absoluteValueCalculator({ number: params.number })
    },
    logarithmCalculator: (params: Record<string, any>) => {
        return logarithmCalculator({ number: params.number, base: params.base })
    },
    exponentialGrowthDecayCalculator: (params: Record<string, any>) => {
        return exponentialGrowthDecayCalculator({ initial_value: params.initial_value, rate_percent: params.rate_percent, time_periods: params.time_periods })
    },
    perfectSquareCubeChecker: (params: Record<string, any>) => {
        return perfectSquareCubeChecker({ number: params.number })
    },
    rationalExponentCalculator: (params: Record<string, any>) => {
        return rationalExponentCalculator({ base: params.base, numerator: params.numerator, denominator: params.denominator })
    },
    rootMeanSquareCalculator: (params: Record<string, any>) => {
        return rootMeanSquareCalculator({ values_list: params.values_list })
    },
    quadraticEquationSolver: (params: Record<string, any>) => {
        return quadraticEquationSolver({ a: params.a, b: params.b, c: params.c })
    },
    linearEquationSolver: (params: Record<string, any>) => {
        return linearEquationSolver({ a: params.a, b: params.b, c: params.c })
    },
    systemOfLinearEquationsSolver: (params: Record<string, any>) => {
        return systemOfLinearEquationsSolver({ a1: params.a1, b1: params.b1, c1: params.c1, a2: params.a2, b2: params.b2, c2: params.c2 })
    },
    slopeCalculator: (params: Record<string, any>) => {
        return slopeCalculator({ x1: params.x1, y1: params.y1, x2: params.x2, y2: params.y2 })
    },
    distanceBetweenPointsCalculator: (params: Record<string, any>) => {
        return distanceBetweenPointsCalculator({ x1: params.x1, y1: params.y1, x2: params.x2, y2: params.y2 })
    },
    midpointCalculator: (params: Record<string, any>) => {
        return midpointCalculator({ x1: params.x1, y1: params.y1, x2: params.x2, y2: params.y2 })
    },
    pointSlopeFormCalculator: (params: Record<string, any>) => {
        return pointSlopeFormCalculator({ x1: params.x1, y1: params.y1, slope: params.slope })
    },
    parabolaVertexCalculator: (params: Record<string, any>) => {
        return parabolaVertexCalculator({ a: params.a, b: params.b, c: params.c })
    },
    discriminantCalculator: (params: Record<string, any>) => {
        return discriminantCalculator({ a: params.a, b: params.b, c: params.c, language: params.language })
    },
    completingTheSquareCalculator: (params: Record<string, any>) => {
        return completingTheSquareCalculator({ a: params.a, b: params.b, c: params.c })
    },
    quadraticFactoringCalculator: (params: Record<string, any>) => {
        return quadraticFactoringCalculator({ a: params.a, b: params.b, c: params.c })
    },
    binomialMultiplicationCalculator: (params: Record<string, any>) => {
        return binomialMultiplicationCalculator({ a1: params.a1, b1: params.b1, a2: params.a2, b2: params.b2 })
    },
    longDivisionCalculator: (params: Record<string, any>) => {
        return longDivisionCalculator({ dividend: params.dividend, divisor: params.divisor })
    },
    longMultiplicationCalculator: (params: Record<string, any>) => {
        return longMultiplicationCalculator({ number1: params.number1, number2: params.number2 })
    },
    longAdditionCalculator: (params: Record<string, any>) => {
        return longAdditionCalculator({ numbers_list: params.numbers_list })
    },
    longSubtractionCalculator: (params: Record<string, any>) => {
        return longSubtractionCalculator({ number1: params.number1, number2: params.number2 })
    },
    gcdCalculator: (params: Record<string, any>) => {
        return gcdCalculator({ numbers_list: params.numbers_list })
    },
    lcmCalculator: (params: Record<string, any>) => {
        return lcmCalculator({ numbers_list: params.numbers_list })
    },
    primeFactorizationCalculator: (params: Record<string, any>) => {
        return primeFactorizationCalculator({ number: params.number })
    },
    primeNumberChecker: (params: Record<string, any>) => {
        return primeNumberChecker({ number: params.number })
    },
    moduloCalculator: (params: Record<string, any>) => {
        return moduloCalculator({ dividend: params.dividend, divisor: params.divisor })
    },
    divisibilityChecker: (params: Record<string, any>) => {
        return divisibilityChecker({ number: params.number })
    },
    digitSumCalculator: (params: Record<string, any>) => {
        return digitSumCalculator({ number: params.number })
    },
    roundingCalculator: (params: Record<string, any>) => {
        return roundingCalculator({ number: params.number, decimal_places: params.decimal_places })
    },
    fractionSimplifier: (params: Record<string, any>) => {
        return fractionSimplifier({ numerator: params.numerator, denominator: params.denominator })
    },
    fractionToDecimalCalculator: (params: Record<string, any>) => {
        return fractionToDecimalCalculator({ numerator: params.numerator, denominator: params.denominator })
    },
    decimalToFractionCalculator: (params: Record<string, any>) => {
        return decimalToFractionCalculator({ decimal: params.decimal })
    },
    fractionToPercentCalculator: (params: Record<string, any>) => {
        return fractionToPercentCalculator({ numerator: params.numerator, denominator: params.denominator })
    },
    percentToFractionCalculator: (params: Record<string, any>) => {
        return percentToFractionCalculator({ percent: params.percent })
    },
    addingFractionsCalculator: (params: Record<string, any>) => {
        return addingFractionsCalculator({ numerator1: params.numerator1, denominator1: params.denominator1, numerator2: params.numerator2, denominator2: params.denominator2 })
    },
    subtractingFractionsCalculator: (params: Record<string, any>) => {
        return subtractingFractionsCalculator({ numerator1: params.numerator1, denominator1: params.denominator1, numerator2: params.numerator2, denominator2: params.denominator2 })
    },
    multiplyingFractionsCalculator: (params: Record<string, any>) => {
        return multiplyingFractionsCalculator({ numerator1: params.numerator1, denominator1: params.denominator1, numerator2: params.numerator2, denominator2: params.denominator2 })
    },
    dividingFractionsCalculator: (params: Record<string, any>) => {
        return dividingFractionsCalculator({ numerator1: params.numerator1, denominator1: params.denominator1, numerator2: params.numerator2, denominator2: params.denominator2 })
    },
    mixedImproperConverter: (params: Record<string, any>) => {
        return mixedImproperConverter({ mode: params.mode, whole_number: params.whole_number, numerator: params.numerator, denominator: params.denominator })
    },
    comparingFractionsCalculator: (params: Record<string, any>) => {
        return comparingFractionsCalculator({ numerator1: params.numerator1, denominator1: params.denominator1, numerator2: params.numerator2, denominator2: params.denominator2, language: params.language })
    },
    fractionOfNumberCalculator: (params: Record<string, any>) => {
        return fractionOfNumberCalculator({ numerator: params.numerator, denominator: params.denominator, number: params.number })
    },
    factorsCalculator: (params: Record<string, any>) => {
        return factorsCalculator({ number: params.number })
    },
    primeNumbersGenerator: (params: Record<string, any>) => {
        return primeNumbersGenerator({ limit: params.limit })
    },
    oddEvenChecker: (params: Record<string, any>) => {
        return oddEvenChecker({ number: params.number, language: params.language })
    },
    perfectNumberChecker: (params: Record<string, any>) => {
        return perfectNumberChecker({ number: params.number, language: params.language })
    },
    fibonacciSequenceCalculator: (params: Record<string, any>) => {
        return fibonacciSequenceCalculator({ term_count: params.term_count })
    },
    coprimeChecker: (params: Record<string, any>) => {
        return coprimeChecker({ number1: params.number1, number2: params.number2, language: params.language })
    },
    sumOfDivisorsCalculator: (params: Record<string, any>) => {
        return sumOfDivisorsCalculator({ number: params.number })
    },
    numberOfDivisorsCalculator: (params: Record<string, any>) => {
        return numberOfDivisorsCalculator({ number: params.number })
    },
    armstrongNumberChecker: (params: Record<string, any>) => {
        return armstrongNumberChecker({ number: params.number, language: params.language })
    },
    triangularNumberCalculator: (params: Record<string, any>) => {
        return triangularNumberCalculator({ n: params.n })
    },
    multiplesCalculator: (params: Record<string, any>) => {
        return multiplesCalculator({ number: params.number, count: params.count })
    },
    palindromicNumberChecker: (params: Record<string, any>) => {
        return palindromicNumberChecker({ number: params.number, language: params.language })
    },
    significantFiguresCalculator: (params: Record<string, any>) => {
        return significantFiguresCalculator({ number: params.number, sig_figs: params.sig_figs })
    },
    roundToNearestMultipleCalculator: (params: Record<string, any>) => {
        return roundToNearestMultipleCalculator({ number: params.number, multiple: params.multiple })
    },
    ceilingFloorCalculator: (params: Record<string, any>) => {
        return ceilingFloorCalculator({ number: params.number, decimal_places: params.decimal_places })
    },
    truncateNumberCalculator: (params: Record<string, any>) => {
        return truncateNumberCalculator({ number: params.number, decimal_places: params.decimal_places })
    },
    engineeringNotationCalculator: (params: Record<string, any>) => {
        return engineeringNotationCalculator({ number: params.number })
    },
    orderOfMagnitudeCalculator: (params: Record<string, any>) => {
        return orderOfMagnitudeCalculator({ number: params.number })
    },
    numberFormatterCalculator: (params: Record<string, any>) => {
        return numberFormatterCalculator({ number: params.number, separator_style: params.separator_style, decimal_places: params.decimal_places })
    },
    ordinalNumberConverter: (params: Record<string, any>) => {
        return ordinalNumberConverter({ number: params.number, language: params.language })
    },
    bankersRoundingCalculator: (params: Record<string, any>) => {
        return bankersRoundingCalculator({ number: params.number, decimal_places: params.decimal_places })
    },
    decimalPlacesCounter: (params: Record<string, any>) => {
        return decimalPlacesCounter({ number: params.number })
    },
    roundToNearestCentCalculator: (params: Record<string, any>) => {
        return roundToNearestCentCalculator({ amount: params.amount })
    },
    repeatingDecimalCalculator: (params: Record<string, any>) => {
        return repeatingDecimalCalculator({ numerator: params.numerator, denominator: params.denominator })
    },
    bettingOddsConverter: (params: Record<string, any>) => {
        return bettingOddsConverter({ format: params.format, decimal_value: params.decimal_value, american_value: params.american_value, fraction_numerator: params.fraction_numerator, fraction_denominator: params.fraction_denominator })
    },
    parlayOddsCalculator: (params: Record<string, any>) => {
        return parlayOddsCalculator({ odds_list: params.odds_list, stake: params.stake })
    },
    functionEvaluator: (params: Record<string, any>) => {
        return functionEvaluator({ expression: params.expression, x_value: params.x_value, language: params.language })
    },
    functionCompositionCalculator: (params: Record<string, any>) => {
        return functionCompositionCalculator({ f_expression: params.f_expression, g_expression: params.g_expression, x_value: params.x_value, language: params.language })
    },
    inverseFunctionFinder: (params: Record<string, any>) => {
        return inverseFunctionFinder({ slope: params.slope, intercept: params.intercept, language: params.language })
    },
    arithmeticSequenceCalculator: (params: Record<string, any>) => {
        return arithmeticSequenceCalculator({ first_term: params.first_term, common_difference: params.common_difference, n: params.n })
    },
    geometricSequenceCalculator: (params: Record<string, any>) => {
        return geometricSequenceCalculator({ first_term: params.first_term, common_ratio: params.common_ratio, n: params.n })
    },
    polynomialLongDivisionCalculator: (params: Record<string, any>) => {
        return polynomialLongDivisionCalculator({ dividend_coefficients: params.dividend_coefficients, divisor_coefficients: params.divisor_coefficients, language: params.language })
    },
    setOperationsCalculator: (params: Record<string, any>) => {
        return setOperationsCalculator({ set_a: params.set_a, set_b: params.set_b })
    },
    truthTableGenerator: (params: Record<string, any>) => {
        return truthTableGenerator({ expression: params.expression, variables: params.variables })
    },
    matrixCalculator2x2: (params: Record<string, any>) => {
        return matrixCalculator2x2({ a11: params.a11, a12: params.a12, a21: params.a21, a22: params.a22, b11: params.b11, b12: params.b12, b21: params.b21, b22: params.b22, operation: params.operation })
    },
    absoluteValueEquationSolver: (params: Record<string, any>) => {
        return absoluteValueEquationSolver({ a: params.a, b: params.b, c: params.c, language: params.language })
    },
    multifactorialCalculator: (params: Record<string, any>) => {
        return multifactorialCalculator({ number: params.number, k: params.k })
    },
    permutationParityChecker: (params: Record<string, any>) => {
        return permutationParityChecker({ permutation: params.permutation, language: params.language })
    },
    circularPermutationsCalculator: (params: Record<string, any>) => {
        return circularPermutationsCalculator({ n: params.n })
    },
    pascalTriangleRowGenerator: (params: Record<string, any>) => {
        return pascalTriangleRowGenerator({ row_number: params.row_number })
    },
    combinationsWithRepetitionCalculator: (params: Record<string, any>) => {
        return combinationsWithRepetitionCalculator({ n: params.n, r: params.r })
    },
    permutationsWithRepetitionCalculator: (params: Record<string, any>) => {
        return permutationsWithRepetitionCalculator({ total_items: params.total_items, group_sizes: params.group_sizes })
    },
    derangementCalculator: (params: Record<string, any>) => {
        return derangementCalculator({ n: params.n })
    },
    catalanNumberCalculator: (params: Record<string, any>) => {
        return catalanNumberCalculator({ n: params.n })
    },
    stirlingSecondKindCalculator: (params: Record<string, any>) => {
        return stirlingSecondKindCalculator({ n: params.n, k: params.k })
    },
    bellNumberCalculator: (params: Record<string, any>) => {
        return bellNumberCalculator({ n: params.n })
    },
    lucasNumbersCalculator: (params: Record<string, any>) => {
        return lucasNumbersCalculator({ term_count: params.term_count })
    },
    integerPartitionCalculator: (params: Record<string, any>) => {
        return integerPartitionCalculator({ n: params.n })
    },
    sineCalculator: (params: Record<string, any>) => {
        return sineCalculator({ angle_value: params.angle_value, angle_unit: params.angle_unit })
    },
    cosineCalculator: (params: Record<string, any>) => {
        return cosineCalculator({ angle_value: params.angle_value, angle_unit: params.angle_unit })
    },
    tangentCalculator: (params: Record<string, any>) => {
        return tangentCalculator({ angle_value: params.angle_value, angle_unit: params.angle_unit, language: params.language })
    },
    cotSecCscCalculator: (params: Record<string, any>) => {
        return cotSecCscCalculator({ angle_value: params.angle_value, angle_unit: params.angle_unit, language: params.language })
    },
    arcsinCalculator: (params: Record<string, any>) => {
        return arcsinCalculator({ value: params.value, language: params.language })
    },
    arccosCalculator: (params: Record<string, any>) => {
        return arccosCalculator({ value: params.value, language: params.language })
    },
    arctanCalculator: (params: Record<string, any>) => {
        return arctanCalculator({ value: params.value })
    },
    rightTriangleSolver: (params: Record<string, any>) => {
        return rightTriangleSolver({ angle_degrees: params.angle_degrees, known_side_type: params.known_side_type, known_side_value: params.known_side_value })
    },
    lawOfSinesCalculator: (params: Record<string, any>) => {
        return lawOfSinesCalculator({ angle_a_degrees: params.angle_a_degrees, side_a: params.side_a, angle_b_degrees: params.angle_b_degrees, language: params.language })
    },
    lawOfCosinesCalculator: (params: Record<string, any>) => {
        return lawOfCosinesCalculator({ side_a: params.side_a, side_b: params.side_b, angle_c_degrees: params.angle_c_degrees, language: params.language })
    },
    unitCircleCalculator: (params: Record<string, any>) => {
        return unitCircleCalculator({ angle_value: params.angle_value, angle_unit: params.angle_unit, language: params.language })
    },
    trigTableGenerator: (params: Record<string, any>) => {
        return trigTableGenerator({ start_angle: params.start_angle, end_angle: params.end_angle, step_degrees: params.step_degrees, language: params.language })
    },
    molecularWeightCalculator: (params: Record<string, any>) => {
        return molecularWeightCalculator({ formula: params.formula, language: params.language })
    },
    percentCompositionCalculator: (params: Record<string, any>) => {
        return percentCompositionCalculator({ formula: params.formula, language: params.language })
    },
    empiricalFormulaCalculator: (params: Record<string, any>) => {
        return empiricalFormulaCalculator({
            element_1: params.element_1, percent_1: params.percent_1,
            element_2: params.element_2, percent_2: params.percent_2,
            element_3: params.element_3, percent_3: params.percent_3,
            language: params.language,
        })
    },
    molecularFormulaCalculator: (params: Record<string, any>) => {
        return molecularFormulaCalculator({ empirical_formula: params.empirical_formula, molar_mass: params.molar_mass, language: params.language })
    },
    molesGramsCalculator: (params: Record<string, any>) => {
        return molesGramsCalculator({ formula: params.formula, mode: params.mode, value: params.value, language: params.language })
    },
    molarityCalculator: (params: Record<string, any>) => {
        return molarityCalculator({ solve_for: params.solve_for, moles: params.moles, volume_liters: params.volume_liters, molarity: params.molarity, language: params.language })
    },
    dilutionCalculator: (params: Record<string, any>) => {
        return dilutionCalculator({ solve_for: params.solve_for, c1: params.c1, v1: params.v1, c2: params.c2, v2: params.v2, language: params.language })
    },
    idealGasLawCalculator: (params: Record<string, any>) => {
        return idealGasLawCalculator({
            solve_for: params.solve_for, pressure_atm: params.pressure_atm, volume_liters: params.volume_liters,
            moles: params.moles, temperature_k: params.temperature_k, language: params.language,
        })
    },
    phCalculator: (params: Record<string, any>) => {
        return phCalculator({ input_type: params.input_type, value: params.value, language: params.language })
    },
    elementLookupTool: (params: Record<string, any>) => {
        return elementLookupTool({ query: params.query, language: params.language })
    },
    densityCalculatorChem: (params: Record<string, any>) => {
        return densityCalculatorChem({ solve_for: params.solve_for, mass_grams: params.mass_grams, volume_ml: params.volume_ml, density: params.density, language: params.language })
    },
    limitingReactantCalculator: (params: Record<string, any>) => {
        return limitingReactantCalculator({
            reactant_a_formula: params.reactant_a_formula, reactant_a_grams: params.reactant_a_grams, reactant_a_coefficient: params.reactant_a_coefficient,
            reactant_b_formula: params.reactant_b_formula, reactant_b_grams: params.reactant_b_grams, reactant_b_coefficient: params.reactant_b_coefficient,
            product_formula: params.product_formula, product_coefficient: params.product_coefficient, language: params.language,
        })
    },
    velocityCalculator: (params: Record<string, any>) => {
        return velocityCalculator({ solve_for: params.solve_for, distance_m: params.distance_m, time_s: params.time_s, velocity_ms: params.velocity_ms, language: params.language })
    },
    accelerationCalculator: (params: Record<string, any>) => {
        return accelerationCalculator({
            solve_for: params.solve_for, initial_velocity: params.initial_velocity, final_velocity: params.final_velocity,
            time_s: params.time_s, acceleration: params.acceleration, language: params.language,
        })
    },
    displacementCalculator: (params: Record<string, any>) => {
        return displacementCalculator({ initial_velocity: params.initial_velocity, acceleration: params.acceleration, time_s: params.time_s, language: params.language })
    },
    finalVelocityCalculator: (params: Record<string, any>) => {
        return finalVelocityCalculator({
            solve_for: params.solve_for, initial_velocity: params.initial_velocity, acceleration: params.acceleration,
            time_s: params.time_s, final_velocity: params.final_velocity, language: params.language,
        })
    },
    suvatThirdEquationCalculator: (params: Record<string, any>) => {
        return suvatThirdEquationCalculator({
            solve_for: params.solve_for, initial_velocity: params.initial_velocity, final_velocity: params.final_velocity,
            acceleration: params.acceleration, displacement: params.displacement, language: params.language,
        })
    },
    freeFallCalculator: (params: Record<string, any>) => {
        return freeFallCalculator({ mode: params.mode, value: params.value, language: params.language })
    },
    projectileMotionCalculator: (params: Record<string, any>) => {
        return projectileMotionCalculator({ initial_speed_ms: params.initial_speed_ms, launch_angle_deg: params.launch_angle_deg, initial_height_m: params.initial_height_m, language: params.language })
    },
    centripetalAccelerationCalculator: (params: Record<string, any>) => {
        return centripetalAccelerationCalculator({ solve_for: params.solve_for, velocity_ms: params.velocity_ms, radius_m: params.radius_m, acceleration_ms2: params.acceleration_ms2, language: params.language })
    },
    relativeVelocityCalculator: (params: Record<string, any>) => {
        return relativeVelocityCalculator({ velocity_a_ms: params.velocity_a_ms, velocity_b_ms: params.velocity_b_ms, language: params.language })
    },
    stoppingDistanceCalculator: (params: Record<string, any>) => {
        return stoppingDistanceCalculator({ initial_velocity_ms: params.initial_velocity_ms, deceleration_ms2: params.deceleration_ms2, reaction_time_s: params.reaction_time_s, language: params.language })
    },
    terminalVelocityCalculator: (params: Record<string, any>) => {
        return terminalVelocityCalculator({
            mass_kg: params.mass_kg, drag_coefficient: params.drag_coefficient,
            cross_sectional_area_m2: params.cross_sectional_area_m2, air_density_kgm3: params.air_density_kgm3, language: params.language,
        })
    },
    generalKinematicsSolver: (params: Record<string, any>) => {
        return generalKinematicsSolver({
            known_1: params.known_1, known_1_value: params.known_1_value,
            known_2: params.known_2, known_2_value: params.known_2_value,
            known_3: params.known_3, known_3_value: params.known_3_value,
            language: params.language,
        })
    },
    forceCalculator: (params: Record<string, any>) => {
        return forceCalculator({ solve_for: params.solve_for, mass_kg: params.mass_kg, acceleration_ms2: params.acceleration_ms2, force_n: params.force_n, language: params.language })
    },
    momentumCalculator: (params: Record<string, any>) => {
        return momentumCalculator({ solve_for: params.solve_for, mass_kg: params.mass_kg, velocity_ms: params.velocity_ms, momentum_kgms: params.momentum_kgms, language: params.language })
    },
    impulseCalculator: (params: Record<string, any>) => {
        return impulseCalculator({
            mass_kg: params.mass_kg, initial_velocity_ms: params.initial_velocity_ms,
            final_velocity_ms: params.final_velocity_ms, time_s: params.time_s, language: params.language,
        })
    },
    weightCalculator: (params: Record<string, any>) => {
        return weightCalculator({ solve_for: params.solve_for, mass_kg: params.mass_kg, gravity_ms2: params.gravity_ms2, weight_n: params.weight_n, language: params.language })
    },
    frictionForceCalculator: (params: Record<string, any>) => {
        return frictionForceCalculator({
            solve_for: params.solve_for, coefficient_of_friction: params.coefficient_of_friction,
            normal_force_n: params.normal_force_n, friction_force_n: params.friction_force_n, language: params.language,
        })
    },
    inclineForceCalculator: (params: Record<string, any>) => {
        return inclineForceCalculator({ mass_kg: params.mass_kg, angle_deg: params.angle_deg, language: params.language })
    },
    workCalculator: (params: Record<string, any>) => {
        return workCalculator({ force_n: params.force_n, distance_m: params.distance_m, angle_deg: params.angle_deg, language: params.language })
    },
    kineticEnergyCalculator: (params: Record<string, any>) => {
        return kineticEnergyCalculator({ solve_for: params.solve_for, mass_kg: params.mass_kg, velocity_ms: params.velocity_ms, kinetic_energy_j: params.kinetic_energy_j, language: params.language })
    },
    potentialEnergyCalculator: (params: Record<string, any>) => {
        return potentialEnergyCalculator({
            solve_for: params.solve_for, mass_kg: params.mass_kg, height_m: params.height_m,
            gravity_ms2: params.gravity_ms2, potential_energy_j: params.potential_energy_j, language: params.language,
        })
    },
    powerCalculator: (params: Record<string, any>) => {
        return powerCalculator({ solve_for: params.solve_for, work_j: params.work_j, time_s: params.time_s, power_w: params.power_w, language: params.language })
    },
    springForceCalculator: (params: Record<string, any>) => {
        return springForceCalculator({
            solve_for: params.solve_for, spring_constant_nm: params.spring_constant_nm,
            displacement_m: params.displacement_m, force_n: params.force_n, language: params.language,
        })
    },
    torqueCalculator: (params: Record<string, any>) => {
        return torqueCalculator({
            solve_for: params.solve_for, force_n: params.force_n, radius_m: params.radius_m,
            angle_deg: params.angle_deg, torque_nm: params.torque_nm, language: params.language,
        })
    },
    mechanicalEnergyCalculator: (params: Record<string, any>) => {
        return mechanicalEnergyCalculator({ mass_kg: params.mass_kg, velocity_ms: params.velocity_ms, height_m: params.height_m, gravity_ms2: params.gravity_ms2, language: params.language })
    },
    workEnergyTheoremCalculator: (params: Record<string, any>) => {
        return workEnergyTheoremCalculator({
            solve_for: params.solve_for, mass_kg: params.mass_kg, initial_velocity_ms: params.initial_velocity_ms,
            final_velocity_ms: params.final_velocity_ms, work_j: params.work_j, language: params.language,
        })
    },
    heatEnergyCalculator: (params: Record<string, any>) => {
        return heatEnergyCalculator({
            solve_for: params.solve_for, mass_kg: params.mass_kg, specific_heat_jkgc: params.specific_heat_jkgc,
            delta_t_c: params.delta_t_c, heat_j: params.heat_j, language: params.language,
        })
    },
    latentHeatCalculator: (params: Record<string, any>) => {
        return latentHeatCalculator({ solve_for: params.solve_for, mass_kg: params.mass_kg, latent_heat_jkg: params.latent_heat_jkg, heat_j: params.heat_j, language: params.language })
    },
    electricalEnergyCalculator: (params: Record<string, any>) => {
        return electricalEnergyCalculator({ power_w: params.power_w, time_hours: params.time_hours, rate_per_kwh: params.rate_per_kwh, language: params.language })
    },
    massEnergyEquivalenceCalculator: (params: Record<string, any>) => {
        return massEnergyEquivalenceCalculator({ solve_for: params.solve_for, mass_kg: params.mass_kg, energy_j: params.energy_j, language: params.language })
    },
    efficiencyCalculator: (params: Record<string, any>) => {
        return efficiencyCalculator({
            solve_for: params.solve_for, energy_output_j: params.energy_output_j,
            energy_input_j: params.energy_input_j, efficiency_percent: params.efficiency_percent, language: params.language,
        })
    },
    escapeVelocityCalculator: (params: Record<string, any>) => {
        return escapeVelocityCalculator({ mass_kg: params.mass_kg, radius_m: params.radius_m, language: params.language })
    },
    energyUnitConverter: (params: Record<string, any>) => {
        return energyUnitConverter({ value: params.value, from_unit: params.from_unit, to_unit: params.to_unit, language: params.language })
    },
    rotationalKineticEnergyCalculator: (params: Record<string, any>) => {
        return rotationalKineticEnergyCalculator({
            solve_for: params.solve_for, moment_of_inertia_kgm2: params.moment_of_inertia_kgm2,
            angular_velocity_rads: params.angular_velocity_rads, kinetic_energy_j: params.kinetic_energy_j, language: params.language,
        })
    },
    windPowerCalculator: (params: Record<string, any>) => {
        return windPowerCalculator({ air_density_kgm3: params.air_density_kgm3, swept_area_m2: params.swept_area_m2, wind_speed_ms: params.wind_speed_ms, language: params.language })
    },
    photonEnergyCalculator: (params: Record<string, any>) => {
        return photonEnergyCalculator({ mode: params.mode, value: params.value, language: params.language })
    },
    waveSpeedCalculator: (params: Record<string, any>) => {
        return waveSpeedCalculator({
            solve_for: params.solve_for, wave_speed_ms: params.wave_speed_ms,
            frequency_hz: params.frequency_hz, wavelength_m: params.wavelength_m, language: params.language,
        })
    },
    wavePeriodCalculator: (params: Record<string, any>) => {
        return wavePeriodCalculator({ solve_for: params.solve_for, period_s: params.period_s, frequency_hz: params.frequency_hz, language: params.language })
    },
    dopplerEffectCalculator: (params: Record<string, any>) => {
        return dopplerEffectCalculator({
            source_frequency_hz: params.source_frequency_hz, sound_speed_ms: params.sound_speed_ms,
            source_velocity_ms: params.source_velocity_ms, observer_velocity_ms: params.observer_velocity_ms, language: params.language,
        })
    },
    speedOfSoundCalculator: (params: Record<string, any>) => {
        return speedOfSoundCalculator({ solve_for: params.solve_for, temperature_c: params.temperature_c, speed_of_sound_ms: params.speed_of_sound_ms, language: params.language })
    },
    decibelCalculator: (params: Record<string, any>) => {
        return decibelCalculator({ solve_for: params.solve_for, intensity_wm2: params.intensity_wm2, decibels: params.decibels, language: params.language })
    },
    snellsLawCalculator: (params: Record<string, any>) => {
        return snellsLawCalculator({
            solve_for: params.solve_for, n1: params.n1, angle1_deg: params.angle1_deg,
            n2: params.n2, angle2_deg: params.angle2_deg, language: params.language,
        })
    },
    standingWaveCalculator: (params: Record<string, any>) => {
        return standingWaveCalculator({
            harmonic_number: params.harmonic_number, string_length_m: params.string_length_m,
            wave_speed_ms: params.wave_speed_ms, language: params.language,
        })
    },
    buoyancyCalculator: (params: Record<string, any>) => {
        return buoyancyCalculator({
            fluid_density_kgm3: params.fluid_density_kgm3, volume_m3: params.volume_m3,
            object_mass_kg: params.object_mass_kg, gravity_ms2: params.gravity_ms2, language: params.language,
        })
    },
    hydrostaticPressureCalculator: (params: Record<string, any>) => {
        return hydrostaticPressureCalculator({
            fluid_density_kgm3: params.fluid_density_kgm3, depth_m: params.depth_m,
            gravity_ms2: params.gravity_ms2, atmospheric_pressure_pa: params.atmospheric_pressure_pa, language: params.language,
        })
    },
    boylesLawCalculator: (params: Record<string, any>) => {
        return boylesLawCalculator({
            solve_for: params.solve_for, pressure1: params.pressure1, volume1: params.volume1,
            pressure2: params.pressure2, volume2: params.volume2, language: params.language,
        })
    },
    specificGravityCalculator: (params: Record<string, any>) => {
        return specificGravityCalculator({
            solve_for: params.solve_for, substance_density_kgm3: params.substance_density_kgm3,
            reference_density_kgm3: params.reference_density_kgm3, specific_gravity_value: params.specific_gravity_value, language: params.language,
        })
    },
    youngsModulusCalculator: (params: Record<string, any>) => {
        return youngsModulusCalculator({
            force_n: params.force_n, cross_section_area_m2: params.cross_section_area_m2,
            original_length_m: params.original_length_m, extension_m: params.extension_m, language: params.language,
        })
    },
    cubeCalculator: (params: Record<string, any>) => {
        return cubeCalculator({ side_a: params.side_a, language: params.language })
    },
    rectangularPrismCalculator: (params: Record<string, any>) => {
        return rectangularPrismCalculator({ length: params.length, width: params.width, height: params.height, language: params.language })
    },
    cylinderCalculator: (params: Record<string, any>) => {
        return cylinderCalculator({ radius: params.radius, height: params.height, language: params.language })
    },
    coneCalculator: (params: Record<string, any>) => {
        return coneCalculator({ radius: params.radius, height: params.height, language: params.language })
    },
    sphereCalculator: (params: Record<string, any>) => {
        return sphereCalculator({ radius: params.radius, language: params.language })
    },
    squarePyramidCalculator: (params: Record<string, any>) => {
        return squarePyramidCalculator({ base_side: params.base_side, height: params.height, language: params.language })
    },
    triangularPrismCalculator: (params: Record<string, any>) => {
        return triangularPrismCalculator({ base: params.base, triangle_height: params.triangle_height, length: params.length, language: params.language })
    },
    hemisphereCalculator: (params: Record<string, any>) => {
        return hemisphereCalculator({ radius: params.radius, language: params.language })
    },
    coneFrustumCalculator: (params: Record<string, any>) => {
        return coneFrustumCalculator({ top_radius: params.top_radius, bottom_radius: params.bottom_radius, height: params.height, language: params.language })
    },
    ellipsoidCalculator: (params: Record<string, any>) => {
        return ellipsoidCalculator({ semi_axis_a: params.semi_axis_a, semi_axis_b: params.semi_axis_b, semi_axis_c: params.semi_axis_c, language: params.language })
    },
    capsuleCalculator: (params: Record<string, any>) => {
        return capsuleCalculator({ radius: params.radius, cylinder_height: params.cylinder_height, language: params.language })
    },
    torusCalculator: (params: Record<string, any>) => {
        return torusCalculator({ tube_radius: params.tube_radius, center_radius: params.center_radius, language: params.language })
    },
    triangleAreaCalculator: (params: Record<string, any>) => {
        return triangleAreaCalculator({ side_a: params.side_a, side_b: params.side_b, side_c: params.side_c, language: params.language })
    },
    pythagoreanTheoremCalculator: (params: Record<string, any>) => {
        return pythagoreanTheoremCalculator({
            solve_for: params.solve_for, leg_a: params.leg_a, leg_b: params.leg_b, hypotenuse_c: params.hypotenuse_c, language: params.language,
        })
    },
    circleCalculator: (params: Record<string, any>) => {
        return circleCalculator({ radius: params.radius, language: params.language })
    },
    circleSectorCalculator: (params: Record<string, any>) => {
        return circleSectorCalculator({ radius: params.radius, angle_deg: params.angle_deg, language: params.language })
    },
    rectangleCalculator: (params: Record<string, any>) => {
        return rectangleCalculator({ length: params.length, width: params.width, language: params.language })
    },
    squareCalculator: (params: Record<string, any>) => {
        return squareCalculator({ side_a: params.side_a, language: params.language })
    },
    parallelogramCalculator: (params: Record<string, any>) => {
        return parallelogramCalculator({ base: params.base, height: params.height, side_b: params.side_b, language: params.language })
    },
    trapezoidCalculator: (params: Record<string, any>) => {
        return trapezoidCalculator({
            base_a: params.base_a, base_b: params.base_b, height: params.height, leg_c: params.leg_c, leg_d: params.leg_d, language: params.language,
        })
    },
    rhombusCalculator: (params: Record<string, any>) => {
        return rhombusCalculator({ diagonal_p: params.diagonal_p, diagonal_q: params.diagonal_q, language: params.language })
    },
    regularPolygonCalculator: (params: Record<string, any>) => {
        return regularPolygonCalculator({ num_sides: params.num_sides, side_length: params.side_length, language: params.language })
    },
    ellipseCalculator: (params: Record<string, any>) => {
        return ellipseCalculator({ semi_axis_a: params.semi_axis_a, semi_axis_b: params.semi_axis_b, language: params.language })
    },
    kiteCalculator: (params: Record<string, any>) => {
        return kiteCalculator({ diagonal_p: params.diagonal_p, diagonal_q: params.diagonal_q, side_a: params.side_a, side_b: params.side_b, language: params.language })
    },
}

/**
 * Единый движок для JSON калькуляторов
 * Используется на сервере и клиенте для консистентности
 */
export function calculate(
    config: JsonEngineConfig,
    inputValues: JsonEngineInput
): JsonEngineOutput {
    // 1. Подготавливаем переменные (превращаем строки в числа где нужно)
    const scope: Record<string, number | string> = {}
    config.inputs.forEach((inp) => {
        const inputValue = inputValues[inp.key]
        if (inputValue !== undefined && inputValue !== null && inputValue !== '') {
            scope[inp.key] = inputValue
        } else if (inp.default !== undefined) {
            scope[inp.key] = inp.default
        }
    })

    // 2. Обрабатываем результаты
    const results: JsonEngineOutput = {}

    // 2a. Обрабатываем формулы (если есть)
    if (config.formulas) {
        Object.entries(config.formulas).forEach(([outKey, formula]) => {
            try {
                // Для формул конвертируем все в числа
                const numericScope: Record<string, number> = {}
                Object.entries(scope).forEach(([key, val]) => {
                    numericScope[key] = typeof val === 'number' ? val : parseFloat(String(val)) || 0
                })
                
                const rawResult = math.evaluate(formula as string, numericScope)
                // Проверяем, что результат валидный
                if (typeof rawResult === 'number' && !isNaN(rawResult) && isFinite(rawResult)) {
                    results[outKey] = rawResult
                } else {
                    console.error(`Invalid result for ${outKey}:`, rawResult)
                    results[outKey] = 0
                }
            } catch (e) {
                console.error(`Ошибка вычисления формулы ${outKey}:`, e)
                results[outKey] = 0
            }
        })
    }

    // 2b. Обрабатываем кастомные функции (если есть)
    if (config.functions) {
        Object.entries(config.functions).forEach(([outKey, funcConfig]) => {
            try {
                if (funcConfig.type === 'function' && FUNCTION_REGISTRY[funcConfig.functionName]) {
                    // Подготавливаем параметры для функции
                    const params: Record<string, any> = {}
                    // Добавляем язык из конфига, если он не передан явно
                    if (config.language && !params.language) {
                        params.language = config.language
                    }
                    // Добавляем IP посетителя из конфига (для калькулятора "Мой IP-адрес")
                    if ((config as any).client_ip && !params.client_ip) {
                        params.client_ip = (config as any).client_ip
                    }
                    Object.entries(funcConfig.params).forEach(([paramName, inputKey]) => {
                        const value = scope[inputKey]
                        // Для числовых параметров конвертируем в число
                        if (paramName === 'vatRate' && value !== undefined && value !== null) {
                            params[paramName] = typeof value === 'number' ? value : parseFloat(String(value)) || 0
                        } else {
                            params[paramName] = value
                        }
                    })
                    
                    // Вызываем функцию
                    const functionResult = FUNCTION_REGISTRY[funcConfig.functionName](params)
                    
                    // Обрабатываем результат
                    if (typeof functionResult === 'object' && functionResult !== null) {
                        // Если функция возвращает объект (например, { textResult, calculatedTotal })
                        // Извлекаем все поля из объекта в results
                        Object.entries(functionResult).forEach(([key, value]) => {
                            results[key] = value as string | number
                        })
                    } else {
                        results[outKey] = functionResult as string | number
                    }
                }
            } catch (e) {
                console.error(`Ошибка выполнения функции ${outKey}:`, e)
            }
        })
    }

    return results
}

/**
 * Валидация входных данных
 */
export function validate(
    config: JsonEngineConfig,
    inputValues: JsonEngineInput
): { valid: boolean; errors: string[] } {
    const errors: string[] = []

    // Проверяем наличие всех обязательных inputs
    config.inputs.forEach((inp) => {
        const value = inputValues[inp.key]
        if (value === undefined || value === null || value === '') {
            if (inp.default === undefined) {
                errors.push(`Missing required input: ${inp.key}`)
            }
        } else {
            const numValue = typeof value === 'number' ? value : parseFloat(String(value))
            if (isNaN(numValue)) {
                errors.push(`Invalid number for input: ${inp.key}`)
            }
        }
    })

    return {
        valid: errors.length === 0,
        errors
    }
}
